/**
 * User Profile Header Component
 * 
 * Displays user profile header with avatar, name, and basic information.
 */

import * as React from "react";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { 
  User as UserIcon, MapPin, Shield, UserCircle, FileText
} from "lucide-react";
import type { User } from "@/services/authService";
import { getUserInitials } from "../utils/userDetailUtils";

interface UserProfileHeaderProps {
  user: User;
}

export function UserProfileHeader({ user }: UserProfileHeaderProps) {
  const initials = getUserInitials(user);

  return (
    <Card className="overflow-hidden">
      <div className="h-20 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent" />
      <CardContent className="pt-0 pb-6 -mt-10">
        <div className="flex flex-col md:flex-row gap-5 items-start md:items-end">
          <Avatar
            src={user.foto}
            alt={user.name || user.callsign || "User"}
            fallback={initials}
            className="h-24 w-24 ring-4 ring-background shadow-lg"
          >
            <UserIcon className="h-10 w-10" />
          </Avatar>
          
          <div className="flex-1 space-y-2 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <h1 className="text-2xl font-bold truncate">{user.name || "Nama tidak tersedia"}</h1>
              <div className="flex flex-wrap gap-1.5">
                {user.isSilentKey && (
                  <Badge variant="destructive">Silent Key</Badge>
                )}
                {user.status_keanggotaan && (
                  <Badge variant={user.status_keanggotaan === 1 ? "default" : "secondary"}>
                    {user.status_keanggotaan === 1 ? "Aktif" : user.status_keanggotaan === 2 ? "Non-Aktif" : `Status ${user.status_keanggotaan}`}
                  </Badge>
                )}
                {user.status_callsign && (
                  <Badge variant="outline">{user.status_callsign}</Badge>
                )}
              </div>
            </div>
            
            <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
              {user.callsign && (
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <UserCircle className="h-3.5 w-3.5" />
                  <span className="font-medium text-foreground">{user.callsign}</span>
                </div>
              )}
              {user.iar && (
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <FileText className="h-3.5 w-3.5" />
                  <span className="font-medium text-foreground">{user.iar}</span>
                </div>
              )}
              {(user.provinsi || user.kabupaten) && (
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5" />
                  <span>
                    {[user.kabupaten, user.provinsi].filter(Boolean).join(", ") || "-"}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
